import type { DatosDeHoja } from "@gestiondoc/plantilla";
import type { Contenido } from "@gestiondoc/shared";
import { useQuery } from "@tanstack/react-query";
import { useTRPC } from "../api.ts";
import { EstadoVacio } from "../componentes/ui.tsx";
import { useFlujogramas } from "./mermaid.ts";
import { VistaHoja } from "./VistaHoja.tsx";

/** Lo que devuelve `contenido.obtener`, en la forma que la hoja necesita. */
export function datosDeHoja(
  o: {
    documento: {
      codigo: string;
      nombre: string;
      tipo: { requiereAprobacion: boolean };
    };
    revision: { numero: number; estado: string; fecha: Date; idSello: string | null };
    firmas: {
      etapa: "elaboro" | "reviso" | "aprobo";
      nombre: string;
      puesto: string;
      firmadoEn: Date | null;
      imagenId: string | null;
      idFirma: string | null;
    }[];
    historial: {
      revision: number;
      fecha: Date;
      quien: string;
      puesto: string;
      descripcion: string;
    }[];
  },
  contenido: Contenido,
): DatosDeHoja {
  return {
    codigo: o.documento.codigo,
    nombre: o.documento.nombre,
    revision: o.revision.numero,
    fecha: o.revision.fecha,
    contenido,
    firmas: o.firmas.map((f) => ({
      etapa: f.etapa,
      nombre: f.nombre,
      puesto: f.puesto,
      firmadoEn: f.firmadoEn,
      imagenUrl: f.imagenId ? `/archivos/${f.imagenId}` : null,
      idFirma: f.idFirma,
    })),
    idSello: o.revision.idSello,
    borrador: o.revision.estado !== "vigente" && o.revision.estado !== "obsoleta",
    logoUrl: "/marca/logo.png",
    urlDeArchivo: (id) => `/archivos/${id}`,
    historial: o.historial,
    informativo: !o.documento.tipo.requiereAprobacion,
  };
}

/**
 * La hoja de una revisión redactada, de solo lectura: es lo que ven quien revisa y quien
 * aprueba en la pantalla 03 antes de firmar (el equivalente del visor de PDF de una revisión
 * con archivo). El PDF del worker llega en el paso 6; hasta entonces, esto es la vista previa.
 */
export function VistaDeContenido({ revisionId }: { revisionId: string }) {
  const trpc = useTRPC();
  const q = useQuery(trpc.contenido.obtener.queryOptions({ revisionId }));
  const svgs = useFlujogramas(q.data?.contenido);
  if (q.isPending) return <EstadoVacio>Cargando el contenido…</EstadoVacio>;
  if (q.isError) return <EstadoVacio>{q.error.message}</EstadoVacio>;
  return <VistaHoja datos={{ ...datosDeHoja(q.data, q.data.contenido), svgs }} />;
}
